import { Check, Timer, Watch } from 'lucide-react'
import type React from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import type { CalendarEntry } from '../../types/database.tables'
import type { WorkoutWithData } from '../../types/workout'
import { formatDurationMin } from '../../utils/dateUtils'
import { formatDeviceAttribution } from '../../utils/deviceUtils'
import { getDisciplineColor } from '../../utils/disciplineColors'
import { getEntryIcon } from '../../utils/disciplineIcons'

const WorkoutComplete = styled.span<{ $completed: boolean }>`
  display: ${(props) => (props.$completed ? 'inline' : 'none')};
  float: right;
  font-size: ${theme.typography.xs};
  line-height: 1.2;
  margin-left: ${theme.spacing.xs};
`

const WorkoutItemStyled = styled.div<{
  discipline?: string
  completed: boolean
  $isDragging?: boolean
  $showDisciplineColors?: boolean
}>`
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.sm};
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-top: 4px solid ${(props) => (props.$showDisciplineColors ? getWorkoutAccentColor(props.discipline) : props.theme.colors.border)};
  opacity: ${(props) => {
    if (props.$isDragging) return 0.5
    return 1
  }};
  cursor: pointer;
  transition: ${theme.transitions.default};
  margin-bottom: 2px;
  position: relative;
  overflow: hidden; /* Hide overflow content */
  user-select: none;
  min-width: 0; /* Allow shrinking below content width */
  min-height: 75px; /* looks cleaner */

  &:active {
    cursor: grabbing;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }

  ${(props) =>
    props.$isDragging &&
    `
    transform: rotate(5deg);
    z-index: 1000;
    box-shadow: ${theme.shadows.lg};
  `}


  @media (max-width: 768px) {
    border: 2px solid ${({ theme }) => theme.colors.border};
  }
`

const WorkoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2px;
  min-width: 0; /* Allow shrinking below content width */
`

const EntryTitle = styled.div`
  font-weight: 500;
  margin-bottom: 2px;
  position: relative;
  min-width: 0; /* Allow shrinking below content width */
  min-height: 44px;
  overflow: hidden; /* Hide overflow content */
`

const EntryDuration = styled.div`
  font-size: ${theme.typography.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  width: fit-content;
  display: flex;
`

const EntrySource = styled.div`
  font-size: ${theme.typography.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  width: fit-content;
  display: flex;
`

const EntryIcon = styled.div<{ $disciplineColor?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$disciplineColor || props.theme.colors.textSecondary};
  margin-right: ${theme.spacing.xs};
`

const TitleText = styled.span`
  display: inline;
  line-height: 1.2;
  word-wrap: break-word;
`

const WorkoutDetails = styled.div`
  font-size: ${theme.typography.xs};
  display: flex;
  gap: ${theme.spacing.xs};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0; /* Allow shrinking below content width */
  flex-direction: column;
`

// Helper functions
const getWorkoutAccentColor = (type?: string): string => {
  return getDisciplineColor(type)
}

// Props interfaces
interface WorkoutItemProps {
  workout?: WorkoutWithData
  entry?: CalendarEntry
  isDragging?: boolean
  showDisciplineColors?: boolean
  onDragStart?: (e: React.DragEvent, id: string) => void
  onDragEnd?: () => void
  onMouseDown?: (e: React.MouseEvent) => void
  onClick?: (e: React.MouseEvent) => void
  onEdit: () => void
  onDelete: (e: React.MouseEvent, id: string) => void
}

export const WorkoutItem: React.FC<WorkoutItemProps> = ({
  workout,
  entry,
  isDragging = false,
  showDisciplineColors = true,
  onDragStart,
  onDragEnd,
  onMouseDown,
  onClick,
}) => {
  const isWorkout = !!workout
  const item = workout || entry!
  const workoutSession = workout?.workout_sessions?.[0]
  const discipline = workout?.activity_type ?? entry?.entry_type
  const completed = workout?.status === 'completed' || false

  const EntryTypeIconComponent = getEntryIcon(discipline)
  const disciplineColor = getWorkoutAccentColor(discipline)

  const executedTotalDurationMinutes = workout?.executedTotalDurationSeconds
    ? Math.round(workout.executedTotalDurationSeconds / 60)
    : 0
  const ExecutionIcon = Check
  let executionColor = theme.colors.secondaryDark
  if (isWorkout && workout.duration_minutes) {
    const executionTimeDiffPercent =
      Math.abs(executedTotalDurationMinutes - workout.duration_minutes) / workout.duration_minutes
    if (executionTimeDiffPercent > 0.1) {
      // ExecutionIcon = BadgeAlert
      executionColor = theme.colors.warning
    }
  }

  return (
    <WorkoutContainer>
      <WorkoutItemStyled
        discipline={discipline}
        completed={completed}
        $isDragging={isDragging}
        $showDisciplineColors={showDisciplineColors}
        draggable={true}
        onDragStart={onDragStart ? (e) => onDragStart(e, item.id) : undefined}
        onDragEnd={onDragEnd}
        onMouseDown={onMouseDown}
        onClick={onClick}
      >
        <EntryTitle>
          {isWorkout && (
            <WorkoutComplete $completed={completed}>
              <ExecutionIcon size={14} color={theme.colors.secondaryDark} />
            </WorkoutComplete>
          )}
          <EntryIcon $disciplineColor={showDisciplineColors ? disciplineColor : undefined}>
            <EntryTypeIconComponent size={14} />
          </EntryIcon>
          <TitleText>{item.title || workout?.activity_type}</TitleText>
        </EntryTitle>

        <WorkoutDetails>
          {isWorkout && workout && (
            <EntryDuration style={{ color: executionColor }}>
              <Timer size={18} style={{ paddingTop: theme.spacing.xs, paddingRight: theme.spacing.xs }} />
              {workout.executedTotalDurationSeconds !== undefined &&
                formatDurationMin(workout.executedTotalDurationSeconds / 60)}
            </EntryDuration>
          )}
          {/*isWorkout && workout?.time && (
            <EntryTime>
              <Clock size={18} style={{ paddingTop: theme.spacing.xs, paddingRight: theme.spacing.xs }} />
              {hhmm(workout.time)}
            </EntryTime>
          )*/}
          {workoutSession?.device_model && (
            <EntrySource>
              <Watch size={16} />
              {formatDeviceAttribution(workoutSession?.device_model, workoutSession?.source)}
            </EntrySource>
          )}
        </WorkoutDetails>
      </WorkoutItemStyled>
    </WorkoutContainer>
  )
}
